/**
 * The difference between "one panel is broken" and "the site is gone".
 *
 * WHY THIS EXISTS. React unmounts the entire tree when a render throws, and
 * there was nothing here to catch it. The whole panel therefore rendered as a
 * blank dark page whose only clue was in the browser console, which is where a
 * customer never looks and an operator only looks if somebody tells them the
 * page is blank.
 *
 * The bug that produced it is one that will happen again by construction: the
 * API gained a field, the panel started reading it, and the deployed API was a
 * build older than the deployed panel. Any browser tab left open across a
 * deploy is the same skew. A missing field must cost a card, not the session.
 *
 * WHAT IT DELIBERATELY DOES NOT DO. It does not retry, and it does not hide the
 * failure. A reload is offered because a stale bundle is the common cause and a
 * reload actually fixes that one.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Shown instead of the default card, for boundaries around a single panel. */
  fallback?: ReactNode;
  /** Named in the console so the failing area is identifiable. */
  label?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console -- the whole point is that this is findable
    console.error(`[${this.props.label ?? 'panel'}] render failed`, error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback;

    return (
      <div
        role="alert"
        className="rounded-xl border border-rule-strong bg-ink-900 p-6 text-sm text-ink-secondary"
      >
        <p className="font-semibold text-ink-primary">This part of the page could not be shown.</p>
        <p className="mt-2">
          Reloading usually fixes it. If it keeps happening, contact support and mention what you
          were looking at.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-4 rounded-lg border border-rule-strong px-4 py-2 font-medium text-ink-primary transition-colors hover:bg-ink-800"
        >
          Reload
        </button>
      </div>
    );
  }
}
